import { AimOutlined } from '@ant-design/icons';
import type { HqApi } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { buildRegionCascade, findRegionPath } from '@hq/utils';
import { useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Cascader,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  type FormInstance
} from 'antd';
import { useEffect, useMemo, useState } from 'react';

/** 字段名约定：省/市/区 + 详细地址 + 经纬度（各主数据表同名列，后端按名接收）。 */
export const REGION_ADDRESS_FIELDS = {
  codes: 'regionCodes',
  province: 'provinceCode',
  city: 'cityCode',
  district: 'districtCode',
  address: 'address',
  lng: 'lng',
  lat: 'lat'
} as const;

export interface RegionAddressFieldsProps {
  /** 各端注入的接口客户端（共享包不读端内 session）。 */
  hq: HqApi;
  form: FormInstance;
  /** 必填（机构 / 医共体 / 运营仓）；院内仓库可传 false。 */
  required?: boolean;
  disabled?: boolean;
  /** 地址留空时的高德解析提示（不同实体口径不同）。 */
  addressHint?: string;
}

/**
 * 省 / 市 / 区级联 + 详细地址 + 经纬度（高德地理编码）。
 * 区划源为 `sys_region`（由 web-system「行政区划」页一键导入全国数据），
 * 选中区划后把 provinceCode / cityCode / districtCode 写回表单，供后端落库。
 */
export function RegionAddressFields({ hq, form, required = true, disabled, addressHint }: RegionAddressFieldsProps) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);

  const regions = useQuery({
    queryKey: ['sys', 'region', 'options'],
    queryFn: () => hq.tenant.regionOptions(),
    staleTime: 10 * 60 * 1000
  });

  const cascaderOptions = useMemo(
    () => buildRegionCascade(regions.data ?? []),
    [regions.data]
  );

  const district = Form.useWatch(REGION_ADDRESS_FIELDS.district, form) as string | undefined;
  const codes = Form.useWatch(REGION_ADDRESS_FIELDS.codes, form) as string[] | undefined;
  const lng = Form.useWatch(REGION_ADDRESS_FIELDS.lng, form) as number | null | undefined;
  const lat = Form.useWatch(REGION_ADDRESS_FIELDS.lat, form) as number | null | undefined;

  // 编辑态：父表单只回填了 districtCode（级联值为空），这里按区划树补出显示值；
  // 依赖 codes 是为了同一弹窗切换到另一条记录（district 相同时）也能重新补全
  useEffect(() => {
    if (!district || codes?.length || !regions.data?.length) return;
    const path = findRegionPath(regions.data, district);
    if (path.length) form.setFieldValue(REGION_ADDRESS_FIELDS.codes, path);
  }, [district, codes, regions.data, form]);

  const geocode = async (silent = false) => {
    const address = String(form.getFieldValue(REGION_ADDRESS_FIELDS.address) ?? '').trim();
    if (!address) {
      if (!silent) message.warning('请先填写详细地址');
      return;
    }

    setLoading(true);
    try {
      const point = await hq.geo.geocode({
        address,
        city: form.getFieldValue(REGION_ADDRESS_FIELDS.district) || undefined
      });
      form.setFieldsValue({ [REGION_ADDRESS_FIELDS.lng]: point.lng, [REGION_ADDRESS_FIELDS.lat]: point.lat });
      message.success(`已解析坐标：${point.lng}, ${point.lat}`);
    } catch (err) {
      // 静默模式（地址失焦自动解析）失败不打扰，用户仍可点按钮重试
      if (!silent) message.error(err instanceof ApiError ? err.friendlyMessage : '坐标解析失败');
    } finally {
      setLoading(false);
    }
  };

  const onAddressBlur = () => {
    // 已有坐标时不覆盖（可能是手工微调过的点位）
    if (lng != null && lat != null) return;
    void geocode(true);
  };

  return (
    <>
      <Space size={16} wrap style={{ width: '100%' }} align="start">
        <Form.Item
          name={REGION_ADDRESS_FIELDS.codes}
          label="省 / 市 / 区县"
          rules={required ? [{ required: true, message: '请选择省 / 市 / 区县' }] : []}
          style={{ width: 380, minWidth: 320 }}
        >
          <Cascader
            disabled={disabled}
            options={cascaderOptions}
            placeholder="选择省 / 市 / 区县"
            showSearch={{
              filter: (input, path) => path.some((option) => String(option.label).includes(input))
            }}
            onChange={(value) => {
              const [province, city, region] = (value ?? []) as string[];
              form.setFieldsValue({
                [REGION_ADDRESS_FIELDS.province]: province,
                [REGION_ADDRESS_FIELDS.city]: city,
                [REGION_ADDRESS_FIELDS.district]: region
              });
            }}
          />
        </Form.Item>
        <Form.Item
          name={REGION_ADDRESS_FIELDS.address}
          label="详细地址"
          rules={required ? [{ required: true, min: 2, message: '请填写详细地址（不少于 2 个字）' }] : []}
          style={{ flex: 1, minWidth: 260 }}
          extra={addressHint ?? '地址用于高德解析经纬度，填写到门牌号更准确'}
        >
          <Input
            disabled={disabled}
            maxLength={255}
            placeholder="如 富春街道桂花路 8 号"
            onBlur={onAddressBlur}
          />
        </Form.Item>
      </Space>

      {/* 级联只负责交互，落库仍走三个独立字段 */}
      <Form.Item name={REGION_ADDRESS_FIELDS.province} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={REGION_ADDRESS_FIELDS.city} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={REGION_ADDRESS_FIELDS.district} hidden>
        <Input />
      </Form.Item>

      <Space size={16} wrap style={{ width: '100%' }} align="start">
        {/* 经纬度列是 decimal(10,6)：范围与 6 位小数在此卡住，越界值落库会被静默截断 */}
        <Form.Item name={REGION_ADDRESS_FIELDS.lng} label="经度（GPS）" style={{ width: 180 }}>
          <InputNumber
            disabled={disabled}
            style={{ width: '100%' }}
            min={-180}
            max={180}
            step={0.000001}
            precision={6}
            placeholder="如 119.960000"
          />
        </Form.Item>
        <Form.Item name={REGION_ADDRESS_FIELDS.lat} label="纬度（GPS）" style={{ width: 180 }}>
          <InputNumber
            disabled={disabled}
            style={{ width: '100%' }}
            min={-90}
            max={90}
            step={0.000001}
            precision={6}
            placeholder="如 30.049000"
          />
        </Form.Item>
        <Form.Item label=" ">
          <Space>
            <Button icon={<AimOutlined />} loading={loading} disabled={disabled} onClick={() => void geocode(false)}>
              按地址解析坐标
            </Button>
            {lng != null && lat != null ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                已定位 {Number(lng).toFixed(6)}, {Number(lat).toFixed(6)}
              </Typography.Text>
            ) : null}
          </Space>
        </Form.Item>
      </Space>
    </>
  );
}

export default RegionAddressFields;
